import { Key } from "react";

import type {
  Vero1ItemEdit,
  Vero1ItemEditRequest,
  Vero1Severity,
} from "shared/interfaces/api/vero1";
import { SEVERITY_ORDER } from "shared/interfaces/api/vero1";

/** Server conflict vocabulary (vero1 409 bodies carry `code`). */
export enum Vero1ConflictCode {
  VersionConflict = "version-conflict",
  DealDecisioned = "deal-decisioned",
  NotADraft = "not-a-draft",
  ItemGone = "item-gone",
}

export type PersistOutcome =
  /** newToken is the draft's fresh token — a queued writer needs it
   *  synchronously rather than racing the cache write. */
  | { status: "saved"; newToken?: string }
  | {
      status: "blocked";
      code: Vero1ConflictCode.DealDecisioned | Vero1ConflictCode.NotADraft;
    }
  /** The draft moved: keep what was typed, save again over the fresh draft. */
  | { status: "conflict"; code: Vero1ConflictCode }
  | {
      status: "rejected";
      code: "severity-unavailable";
      availableSeverities: Vero1Severity[];
    }
  /** The name is not configured at this severity; the server's list stands. */
  | {
      status: "rejected";
      code: "name-unavailable";
      nameCandidates: string[];
    }
  /** Anything else — the mutation layer has already toasted it. */
  | { status: "failed" };

export interface PersistItemInput {
  itemId: Key;
  fields: Vero1ItemEdit;
  /** The draft token the agent was looking at when the editor opened. The
   *  API rejects a write without it. */
  expectedToken: string;
  /** Refuse to dispatch when the review is already closed (F5 write-stop). */
  locked?: boolean;
}

type EditMutation = (
  input: { itemId: Key; edit: Vero1ItemEditRequest },
  handlers: {
    onSuccess: (checklist?: { updatedAt?: string }) => void;
    onError: (error: unknown) => void;
  }
) => void;

export const codeOf = (error: unknown): Vero1ConflictCode | null => {
  // the interceptor lands a 409 body on ApiError.errors; a raw axios failure
  // keeps it on response.data
  const e =
    (error as {
      errors?: { code?: string };
      response?: { data?: { code?: string } };
    }) ?? {};
  const raw = e.errors?.code ?? e.response?.data?.code;
  return raw &&
    Object.values(Vero1ConflictCode).includes(raw as Vero1ConflictCode)
    ? (raw as Vero1ConflictCode)
    : null;
};

const isSeverity = (value: unknown): value is Vero1Severity =>
  SEVERITY_ORDER.includes(value as Vero1Severity);

const RAW_SEVERITIES_KEY = "available_severities";

const availableSeveritiesOf = (error: unknown): Vero1Severity[] | null => {
  const e =
    (error as {
      errors?: Record<string, unknown>;
      response?: { data?: Record<string, unknown> };
    }) ?? {};
  const raw =
    e.errors?.availableSeverities ??
    e.errors?.[RAW_SEVERITIES_KEY] ??
    e.response?.data?.[RAW_SEVERITIES_KEY];
  return Array.isArray(raw) && raw.every(isSeverity) ? raw : null;
};

const RAW_NAMES_KEY = "name_candidates";

const nameCandidatesOf = (error: unknown): string[] | null => {
  const e =
    (error as {
      errors?: Record<string, unknown>;
      response?: { data?: Record<string, unknown> };
    }) ?? {};
  const raw =
    e.errors?.nameCandidates ??
    e.errors?.[RAW_NAMES_KEY] ??
    e.response?.data?.[RAW_NAMES_KEY];
  return Array.isArray(raw) && raw.every((v) => typeof v === "string")
    ? (raw as string[])
    : null;
};

const statusOf = (error: unknown): number | undefined => {
  const e =
    (error as { statusCode?: number; response?: { status?: number } }) ?? {};
  return e.statusCode ?? e.response?.status;
};

/**
 * The one place an item edit is written.
 *
 * Token pinning, conflict classification, and the write-stop live here rather
 * than at the call site so every writer — the Save button today, a debounced
 * queue later — resolves them identically. Callers get an outcome, never a
 * status code.
 */
export const persistItemEdit = (
  editItem: EditMutation,
  { itemId, fields, expectedToken, locked }: PersistItemInput
): Promise<PersistOutcome> => {
  if (locked) {
    return Promise.resolve({
      status: "blocked",
      code: Vero1ConflictCode.DealDecisioned,
    });
  }

  return new Promise((resolve) => {
    editItem(
      {
        itemId,
        // the API version-checks against the draft the agent was looking at
        // when they opened the editor
        edit: { ...fields, expectedUpdatedAt: expectedToken },
      },
      {
        onSuccess: (checklist) =>
          resolve({ status: "saved", newToken: checklist?.updatedAt }),
        onError: (error) => {
          const code = codeOf(error);
          if (code === Vero1ConflictCode.DealDecisioned) {
            resolve({ status: "blocked", code });
            return;
          }
          // a closed review cannot accept a retry; the draft moving can
          if (code === Vero1ConflictCode.NotADraft) {
            resolve({ status: "blocked", code });
            return;
          }
          if (code === Vero1ConflictCode.ItemGone) {
            resolve({ status: "conflict", code });
            return;
          }
          const status = statusOf(error);
          const availableSeverities =
            status === 422 ? availableSeveritiesOf(error) : null;
          if (availableSeverities) {
            resolve({
              status: "rejected",
              code: "severity-unavailable",
              availableSeverities,
            });
            return;
          }
          const nameCandidates =
            status === 422 ? nameCandidatesOf(error) : null;
          if (nameCandidates) {
            resolve({
              status: "rejected",
              code: "name-unavailable",
              nameCandidates,
            });
            return;
          }
          if (code || status === 409 || status === 404 || status === 422) {
            resolve({
              status: "conflict",
              // a body without `code` predates the server contract; the draft
              // moved either way, so recover the same as a version conflict
              code: code ?? Vero1ConflictCode.VersionConflict,
            });
            return;
          }
          resolve({ status: "failed" });
        },
      }
    );
  });
};
